// Story 029 AC10 — Master-delete cascade confirm + bridge.
//
// When the analyst soft-deletes a defect that is the master of one or
// more links, the canonical rule (`.vibe/23` Q10) is to cascade the
// soft-delete to every linked child. The FE confirms the cascade with
// a modal dialog ("Delete this defect AND its N linked siblings?") and
// only fires `DELETE /defects/:id` on Yes. The server is responsible
// for the actual cascade — see docs/backend-requirements/094-defect-links.md.
//
// The bridge mirrors the existing `__aeroUndoBridge` shape from
// AnalysisFeature.tsx — a window-attached function the acceptance spec
// uses to drive the same user-visible dialog that the production UI
// will eventually reach via right-click → Delete on the row (a
// follow-up story extends the row context menu; PR-C ships the
// dialog + cascade plumbing, the row gesture lands in PR-D).
import { useEffect } from "react";
import { useApi } from "@aeriosiq/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { defectKeys, defectLinkKeys } from "../defects/queries.js";
import type { ListDefectLinksResponse } from "../defects/queries.js";

interface DeleteCascadeBridge {
  confirmDeleteWithCascade: (defectId: string) => Promise<void>;
}

interface UseDeleteWithCascadeBridgeArgs {
  dataAcquisitionId: string;
}

export function useDeleteWithCascadeBridge({
  dataAcquisitionId,
}: UseDeleteWithCascadeBridgeArgs): null {
  const api = useApi();
  const qc = useQueryClient();

  useEffect(() => {
    const renderDialog = (
      defectId: string,
      siblingCount: number,
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        const dialog = document.createElement("div");

        const cleanup = () => {
          try {
            document.body.removeChild(dialog);
          } catch {
            // already removed
          }
        };
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute(
          "aria-label",
          `Delete defect and ${String(siblingCount)} linked sibling${siblingCount === 1 ? "" : "s"}`,
        );
        dialog.setAttribute("data-defect-id", defectId);
        dialog.style.position = "fixed";
        dialog.style.inset = "0";
        dialog.style.zIndex = "1500";
        dialog.style.display = "flex";
        dialog.style.alignItems = "center";
        dialog.style.justifyContent = "center";
        dialog.style.background = "rgba(0,0,0,0.5)";

        const card = document.createElement("div");
        card.style.background = "#fff";
        card.style.color = "#000";
        card.style.padding = "24px";
        card.style.borderRadius = "8px";
        card.style.maxWidth = "440px";
        card.style.boxShadow = "0 4px 16px rgba(0,0,0,0.2)";

        const title = document.createElement("div");
        title.style.fontWeight = "600";
        title.style.fontSize = "16px";
        title.style.marginBottom = "8px";
        title.textContent = "Delete linked defects?";
        card.appendChild(title);

        const body = document.createElement("div");
        body.style.fontSize = "14px";
        body.style.marginBottom = "16px";
        body.textContent = `Deleting this defect will also delete ${String(siblingCount)} linked sibling${siblingCount === 1 ? "" : "s"} on other images. Continue?`;
        card.appendChild(body);

        const actions = document.createElement("div");
        actions.style.display = "flex";
        actions.style.justifyContent = "flex-end";
        actions.style.gap = "8px";

        const cancelBtn = document.createElement("button");
        cancelBtn.type = "button";
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.padding = "6px 14px";
        cancelBtn.addEventListener("click", () => {
          cleanup();
          resolve(false);
        });
        actions.appendChild(cancelBtn);

        const continueBtn = document.createElement("button");
        continueBtn.type = "button";
        continueBtn.textContent = "Continue · Delete";
        continueBtn.style.padding = "6px 14px";
        continueBtn.style.background = "#d32f2f";
        continueBtn.style.color = "#fff";
        continueBtn.style.border = "none";
        continueBtn.style.borderRadius = "4px";
        continueBtn.addEventListener("click", () => {
          cleanup();
          resolve(true);
        });
        actions.appendChild(continueBtn);

        card.appendChild(actions);
        dialog.appendChild(card);
        document.body.appendChild(dialog);
        // Focus first action so axe + keyboard pathways stay clean.
        continueBtn.focus();
      });
    };

    const bridge: DeleteCascadeBridge = {
      confirmDeleteWithCascade: async (defectId: string) => {
        // Fetch the current link list so we can show the count in the
        // confirm copy. Cache miss → one network call; cache hit
        // returns instantly.
        const linksResp = await qc.fetchQuery<ListDefectLinksResponse>({
          queryKey: defectLinkKeys.forDefect(defectId),
          queryFn: async () => {
            const r = await api.get<ListDefectLinksResponse>(
              `/defects/${encodeURIComponent(defectId)}/links`,
            );
            return r.data;
          },
        });
        const masterChildren = linksResp.items.filter((l) => l.isMaster);
        const count = masterChildren.length;
        const confirmed = await renderDialog(defectId, count);
        if (!confirmed) return;
        await api.delete<{ deleted: true }>(
          `/defects/${encodeURIComponent(defectId)}`,
        );
        // Invalidate the canonical list + every per-defect link entry
        // we just stale-cascaded so the UI reflects the BE truth.
        await qc.invalidateQueries({
          queryKey: [...defectKeys.all, "list", dataAcquisitionId],
        });
        await qc.invalidateQueries({
          queryKey: defectLinkKeys.forDefect(defectId),
        });
        for (const child of masterChildren) {
          await qc.invalidateQueries({
            queryKey: defectLinkKeys.forDefect(child.siblingDefectId),
          });
        }
      },
    };
    const w = window as unknown as { __aeroLinkBridge?: DeleteCascadeBridge };
    w.__aeroLinkBridge = bridge;
    return () => {
      const w2 = window as unknown as {
        __aeroLinkBridge?: DeleteCascadeBridge;
      };
      delete w2.__aeroLinkBridge;
    };
  }, [api, dataAcquisitionId, qc]);

  return null;
}
